// src/components/Hero.jsx
import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Chip,
} from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import { scrollToSection } from "../utils/scrollToSection";

const Hero = () => (
  <Box
    id="top"
    sx={{
      pt: { xs: 8, sm: 10 },
      pb: { xs: 8, sm: 10 },
      background:
        "linear-gradient(135deg, #0f172a 0%, #020617 40%, #111827 100%)",
      color: "#e5e7eb",
    }}
  >
    <Container maxWidth="lg">
      <Grid container spacing={4} alignItems="center">
        <Grid item xs={12} md={7}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              borderRadius: 999,
              border: "1px solid rgba(129,140,248,0.5)",
              px: 1.6,
              py: 0.5,
              mb: 1.5,
              bgcolor: "rgba(15,23,42,0.9)",
              gap: 1,
              fontSize: 14,
            }}
          >
            <BoltIcon sx={{ fontSize: 18, color: "#a5b4fc" }} />
            Net Zero · Data Integrity · AI Computing
          </Box>
          <Typography
            variant="caption"
            sx={{
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: "#a5b4fc",
            }}
          >
            
          </Typography>
          <Typography
            variant="h3"
            sx={{
              mt: 1,
              mb: 1.5,
              fontWeight: 700,
              fontSize: { xs: 30, sm: 36, md: 44 },
            }}
          >
            Bridging Renewable Energy Data
            <br />
            into Verifiable Carbon Credits
          </Typography>
          <Typography
            sx={{
              color: "#d1d5db",
              mb: 3,
              fontSize: { xs: 14.5, sm: 16 },
              maxWidth: 600,
            }}
          >
            Carbon Bridge Computing turns device-measured renewable energy into
            cryptographically verifiable carbon assets, enabling regulators,
            councils and businesses to trust every kilowatt and every tonne of
            CO₂e.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 1 }}>
            <Button
              variant="contained"
              size="large"
              sx={{ borderRadius: 999 }}
              onClick={() => scrollToSection("contact")}
            >
              Book a Demo
            </Button>
            <Button
              variant="text"
              size="large"
              sx={{ borderRadius: 999, color: "#e5e7eb" }}
              startIcon={<PlayCircleFilledIcon />}
              onClick={() => scrollToSection("about")}
            >
              How it works
            </Button>
          </Box>
          <Typography variant="body2" sx={{ color: "#9ca3af" }}>
            Built for Net Zero, ESG reporting, and high-integrity carbon
            markets.
          </Typography>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box
            className="glass-card glow"
            sx={{
              borderRadius: 3,
              p: 2.5,
              bgcolor: "#111827",
              border: "1px solid rgba(148,163,184,0.3)",
              boxShadow: "0 20px 55px rgba(15,23,42,0.8)",
            }}
          >
            <Chip
              label="Platform Snapshot"
              sx={{
                mb: 1.5,
                bgcolor: "rgba(15,118,110,0.1)",
                color: "#a7f3d0",
              }}
            />
            <Typography variant="subtitle1" sx={{ color: "#e5e7eb", mb: 1 }}>
              End-to-end carbon integrity
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#9ca3af", mb: 2, lineHeight: 1.6 }}
            >
              A deterministic pipeline from energy meter to carbon certificate:
              secure device identity, on-chain tokens, AI analytics and
              audit-ready reports.
            </Typography>

            <Grid container spacing={2} sx={{ mb: 1.5 }}>
              <Grid item xs={6}>
                <Typography
                  variant="overline"
                  sx={{ color: "#9ca3af", letterSpacing: "0.12em" }}
                >
                  Lifecycle
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ color: "#e5e7eb", fontWeight: 600 }}
                >
                  5 stages
                </Typography>
                <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                  Measure → Tokenise → Prove → Certify → Retire
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="overline"
                  sx={{ color: "#9ca3af", letterSpacing: "0.12em" }}
                >
                  Assurance
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ color: "#e5e7eb", fontWeight: 600 }}
                >
                  Mathematically-verifiable grade
                </Typography>
                <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                  Signatures, hashes & multi-chain verifiability
                </Typography>
              </Grid>
            </Grid>

            <Box
              sx={{
                borderTop: "1px solid rgba(55,65,81,0.8)",
                pt: 1.2,
              }}
            >
              <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                Ideal for
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#e5e7eb", mt: 0.5, fontSize: 13.5 }}
              >
                SMEs · Councils · Renewable asset owners · Carbon project
                developers
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

export default Hero;
